import Hls from 'hls.js';
import { useCallback, useEffect, useRef, useState } from 'react';

import { PlayerVideoProps } from '../PlayerVideo.types';

const debug = require('debug')(
  'tca:KitPlayer:components:PlayerVideo:hooks:useEventListeners'
);

const DEFAULT_MAX_MAX_BUFFER_LENGTH = 10 * 60; // 10 minutes of buffer which is the default
const SAFARI_LIVE_MAX_BUFFER_LENGTH = 2 * 60; // 2 minutes of buffer for Safari live streams for faster load times

interface useEventListenersProps extends Partial<PlayerVideoProps> {
  playerRef: React.RefObject<HTMLVideoElement | HTMLAudioElement | null>;
  hlsRef: React.RefObject<Hls | null>;
  onSeeked?: () => void;
  onSeeking?: () => void;
  playMedia: () => void;
  pauseMedia: () => void;
}

interface useEventListenersReturn {
  isPlaying: boolean;
  isReady: boolean;
}

/**
 * useEventListeners hook handles all available events associated with the html video/audio element
 *
 * video - https://developer.mozilla.org/en-US/docs/Web/HTML/Element/video#events
 * audio - https://developer.mozilla.org/en-US/docs/Web/HTML/Element/audio#events
 */
export default ({
  hlsRef,
  onBufferEnd,
  onBufferStart,
  onCanplay,
  onEnd,
  onError,
  onLoadStart,
  onPause,
  onPlay,
  onReady,
  onSeeked,
  onSeeking,

  pauseMedia,
  playerRef,
  playing = true,
  playMedia,
}: useEventListenersProps): useEventListenersReturn => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const hasReloaded = useRef(false);

  const syncPlayStatus = useCallback(
    (currentPlayingState: boolean) => {
      const player = playerRef.current;

      if (!player) return;

      /** media should be paused */
      if (currentPlayingState && !playing) {
        pauseMedia();

        return;
      }

      /** media should be playing */
      if (!currentPlayingState && playing) {
        playMedia();

        return;
      }
    },
    [playerRef, playing, pauseMedia, playMedia]
  );

  const playListener = useCallback(() => {
    debug('playListener');
    onPlay?.();
  }, [onPlay]);

  const waitingListener = useCallback(() => {
    debug('waitingListener');
    onBufferStart?.();
  }, [onBufferStart]);

  const playingListener = useCallback(() => {
    debug('playingListener');
    if (hlsRef.current) {
      // For Safari with infinite duration live streams, use a smaller buffer length
      const isSafariInfiniteLiveStream =
        playerRef.current &&
        (playerRef.current.duration === Infinity ||
          isNaN(playerRef.current.duration));

      hlsRef.current.config.maxMaxBufferLength = isSafariInfiniteLiveStream
        ? SAFARI_LIVE_MAX_BUFFER_LENGTH
        : DEFAULT_MAX_MAX_BUFFER_LENGTH;
    }

    setIsPlaying(true);
    onBufferEnd?.();
    syncPlayStatus(true);
  }, [hlsRef, onBufferEnd, syncPlayStatus, playerRef]);

  const pauseListener = useCallback(() => {
    debug('pauseListener');
    setIsPlaying(false);
    onPause?.();
  }, [onPause]);

  const endedListener = useCallback(() => {
    debug('endedListener');
    onEnd?.();
  }, [onEnd]);

  const errorListener = useCallback(() => {
    debug('errorListener');
    const player = playerRef.current;

    onError?.({ error: player?.error?.message });
  }, [onError, playerRef]);

  const loadStartListener = useCallback(() => {
    debug('loadStartListener');
    setIsReady(false);
    onLoadStart?.();
    onBufferStart?.();
  }, [onBufferStart, onLoadStart]);

  const loadedMetadataListener = useCallback(() => {
    debug('loadedMetadataListener');
    const player = playerRef.current;

    onBufferEnd?.();
    onReady?.({ duration: player?.duration || 0 });
    setIsReady(true);
  }, [playerRef, onBufferEnd, onReady, setIsReady]);

  const loadedDataListener = useCallback(() => {
    debug('loadedDataListener');
  }, []);

  const canplaythroughListener = useCallback(() => {
    debug('canplaythroughListener');
  }, []);

  const canplayListener = useCallback(() => {
    debug('canplayListener');
    const player = playerRef.current;

    if (!player) return;

    const hasInvalidSeekableLength = !player.seekable.length;

    if (!hasReloaded.current && hasInvalidSeekableLength) {
      /**
       * Ensuring seekable.length is not stuck on 0 which will help video player
       * return a proper duration or a seekable range eventually during live
       * streams while using safari native hls
       *
       * Repro:
       * - Navigate to the media detail page before stream starts.
       * - Duration will be infinity and seekable.length will be stuck on 0 for
       * the entire stream unless reload is called.
       * https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/seekable
       */
      debug(
        'canplayListener: attempt to clear out seekable object by reloading player once'
      );
      hasReloaded.current = true;
      player.load();
    } else {
      onCanplay?.();
    }
  }, [playerRef, onCanplay]);

  const seekedListener = useCallback(() => {
    debug('seekedListener');
    onSeeked?.();
  }, [onSeeked]);

  const seekingListener = useCallback(() => {
    debug('seekingListener');
    onSeeking?.();
  }, [onSeeking]);

  useEffect(() => {
    const player = playerRef.current;

    if (!player) return;

    player.addEventListener('canplay', canplayListener);
    player.addEventListener('canplaythrough', canplaythroughListener);
    player.addEventListener('ended', endedListener);
    player.addEventListener('error', errorListener);
    player.addEventListener('loadeddata', loadedDataListener);
    player.addEventListener('loadedmetadata', loadedMetadataListener);
    player.addEventListener('loadstart', loadStartListener);
    player.addEventListener('pause', pauseListener);
    player.addEventListener('play', playListener);
    player.addEventListener('playing', playingListener);
    player.addEventListener('waiting', waitingListener);
    player.addEventListener('seeked', seekedListener);
    player.addEventListener('seeking', seekingListener);

    return () => {
      if (!player) return;

      player.removeEventListener('canplay', canplayListener);
      player.removeEventListener('canplaythrough', canplaythroughListener);
      player.removeEventListener('ended', endedListener);
      player.removeEventListener('error', errorListener);
      player.removeEventListener('loadeddata', loadedDataListener);
      player.removeEventListener('loadedmetadata', loadedMetadataListener);
      player.removeEventListener('loadstart', loadStartListener);
      player.removeEventListener('pause', pauseListener);
      player.removeEventListener('play', playListener);
      player.removeEventListener('playing', playingListener);
      player.removeEventListener('waiting', waitingListener);
      player.removeEventListener('seeked', seekedListener);
      player.removeEventListener('seeking', seekingListener);
    };
  }, [
    canplayListener,
    canplaythroughListener,
    endedListener,
    errorListener,
    loadedDataListener,
    loadedMetadataListener,
    loadStartListener,
    pauseListener,
    playerRef,
    playingListener,
    playListener,
    seekedListener,
    seekingListener,
    waitingListener,
  ]);

  return {
    isPlaying,
    isReady,
  };
};
